<template>
  <BCard class="!rounded-2xl">
    <!-- Header Section -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-[1rem] mb-[1.5rem]">
      <div>
        <div class="text-black text-2xl font-semibold mb-[.5rem]">
          Data Lembur
        </div>
        <p class="text-[#828282] text-sm mb-0">
          Data lembur karyawan untuk monitoring dan pengaturan chain approval.
        </p>
      </div>
      <div>
        <BButton
          variant="primary"
          class="rounded-lg !flex items-center gap-[.5rem]"
          @click="openApprovalChainModal"
        >
          <span class="k-setting-2 text-[18px]" />
          <span>Setting Approval Chain</span>
        </BButton>
      </div>
    </div>

    <!-- Status Tabs Filter Navigation -->
    <div class="flex flex-wrap items-center gap-1.5 bg-[#F5F5F5] p-1.5 rounded-2xl w-fit mb-[1.5rem]">
      <BButton
        v-for="tab in dynamicTabs"
        :key="tab.key"
        variant="light"
        class="transition-all"
        :class="activeTab === tab.key ? 'bg-white text-black shadow-xs' : '!text-[#737373]'"
        @click="onSelectTab(tab.key)"
      >
        {{ tab.label }}
      </BButton>
    </div>

    <!-- Search Input & Export Button Bar -->
    <div class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-[1rem] mb-[1rem]">
      <div class="flex items-center !w-[300px] rounded-[8px] gap-[5px] px-[8px] border-[1px] border-[#E2E2E2] h-[40px]">
        <span class="k-search-normal-1 font-bold text-[20px]" />
        <BFormInput
          v-model="searchQuery"
          placeholder="Cari nama karyawan..."
          class="border-0 grow shadow-none"
          @input="onSearchInput"
        />
      </div>

      <DateRangePicker
        ref="exportPicker"
        v-model="exportDateRange"
        :locale-data="exportLocale"
        :ranges="exportRanges"
        :opens="'left'"
        :auto-apply="false"
        control-container-class="border-0 p-0"
        @update="handleExportSubmit"
      >
        <template #input>
          <BButton
            variant="outline-primary"
            class="rounded-lg !flex items-center justify-center gap-[.5rem] h-[40px]"
            :disabled="isExporting"
          >
            <BSpinner
              v-if="isExporting"
              small
            />
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="shrink-0"
            >
              <path
                stroke="none"
                d="M0 0h24v24H0z"
                fill="none"
              />
              <path d="M8 9h-1a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-8a2 2 0 0 0 -2 -2h-1" />
              <path d="M12 14v-11" />
              <path d="M9 6l3 -3l3 3" />
            </svg>
            <span>{{ isExporting ? 'Mengunduh...' : 'Export Excel' }}</span>
          </BButton>
        </template>
        <template #footer="{ rangeText, clickCancel, clickApply, in_selection }">
          <div class="flex items-center justify-between p-3 border-t border-[#E2E8F0] gap-2 bg-white">
            <span class="text-xs text-[#64748B] font-medium truncate max-w-[200px]">{{ rangeText }}</span>
            <div class="flex items-center gap-2">
              <BButton
                type="button"
                variant="outline-secondary"
                class="!h-[36px] !px-4 !py-0 !rounded-lg text-xs font-medium !border-[#D8D6DE] !text-[#6E6B7B] hover:!bg-[#F8FAFC] hover:!text-[#5E5873] !m-0 !flex items-center justify-center leading-none"
                @click="clickCancel"
              >
                Batal
              </BButton>
              <BButton
                type="button"
                variant="primary"
                class="!h-[36px] !px-4 !py-0 !rounded-lg text-xs font-medium !bg-[#FF6A3A] !border-[#FF6A3A] text-white hover:!bg-[#e5592e] hover:!border-[#e5592e] !m-0 !flex items-center justify-center gap-1.5 leading-none shadow-none"
                :disabled="in_selection || isExporting"
                @click="clickApply"
              >
                <BSpinner
                  v-if="isExporting"
                  small
                />
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="shrink-0"
                >
                  <path
                    stroke="none"
                    d="M0 0h24v24H0z"
                    fill="none"
                  />
                  <path d="M8 9h-1a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-8a2 2 0 0 0 -2 -2h-1" />
                  <path d="M12 14v-11" />
                  <path d="M9 6l3 -3l3 3" />
                </svg>
                <span>Download</span>
              </BButton>
            </div>
          </div>
        </template>
      </DateRangePicker>
    </div>

    <!-- Table Card Wrapper -->
    <div class="p-[1rem] rounded-lg border">
      <div class="flex items-center gap-2.5 mb-[1rem]">
        <span class="text-black text-lg font-medium">
          List Pengajuan Lembur
        </span>
        <BBadge
          variant="light-warning"
          class="text-xs px-2.5 !font-medium py-[.25rem] !rounded-xl text-[#F95000]"
        >
          {{ totalSubmissionsCount }} Pengajuan
        </BBadge>
      </div>

      <Table
        :items="submissions"
        :loading="isLoading"
        :loading-more="isLoadingMore"
        @scroll="handleScroll"
        @action="handleRowAction"
      />
    </div>

    <!-- Detail Drawer -->
    <DetailDrawer
      :visible="detailDrawerVisible"
      :detail="selectedDetail"
      :is-loading="isLoadingDetail"
      @on-close="handleCloseDetailDrawer"
    />
  </BCard>
</template>

<style scoped>
::v-deep .btn-primary,
::v-deep .btn-outline-primary,
::v-deep .btn-light,
::v-deep .btn-link {
  border-radius: 10px !important;
}
</style>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { downloadFileFromBlob } from '@/libs/downloadFile'
import Table from './Table.vue'
import DetailDrawer from './DetailDrawer.vue'
import { statusTabs } from './mockData'
import {
  showSuccess, showError, getAdminOvertimes, getAdminOvertimeDetail, exportAdminOvertimes,
} from './config'

export default {
  name: 'OvertimeList',
  components: {
    Table,
    DetailDrawer,
    DateRangePicker,
  },
  data() {
    return {
      submissions: [],
      tabs: statusTabs,
      activeTab: 'all',
      searchQuery: '',
      searchTimer: null,
      isLoading: false,
      isLoadingDetail: false,
      isExporting: false,
      exportDateRange: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
      exportRanges: {
        'Hari Ini': [moment().toDate(), moment().toDate()],
        '7 Hari Terakhir': [moment().subtract(6, 'days').toDate(), moment().toDate()],
        '30 Hari Terakhir': [moment().subtract(29, 'days').toDate(), moment().toDate()],
        'Bulan Ini': [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
        'Bulan Kemarin': [
          moment().subtract(1, 'month').startOf('month').toDate(),
          moment().subtract(1, 'month').endOf('month').toDate(),
        ],
      },
      exportLocale: {
        direction: 'ltr',
        format: 'YYYY-MM-DD',
        separator: ' - ',
        applyLabel: 'Download',
        cancelLabel: 'Batal',
        weekLabel: 'W',
        customRangeLabel: 'Pilih Sendiri',
        daysOfWeek: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
        monthNames: [
          'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
          'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
        ],
        firstDay: 1,
      },
      detailDrawerVisible: false,
      selectedDetail: {},
      totalApprover: 2,
      divisionApproverCountMap: {},
      page: 1,
      offset: 0,
      limit: 20,
      totalCount: 0,
      lastData: false,
      isLoadingMore: false,
    }
  },
  computed: {
    totalSubmissionsCount() {
      return this.totalCount || this.submissions.length
    },
    dynamicTabs() {
      const count = Math.max(2, Number(this.totalApprover) || 2)
      const tabs = [
        { key: 'all', label: 'Semua' },
      ]

      if (count <= 2) {
        tabs.push({ key: 'approval_1', label: 'Approval 1' })
        tabs.push({ key: 'approval_final', label: 'Approval Final' })
      } else {
        for (let i = 1; i < count; i += 1) {
          tabs.push({ key: `approval_${i}`, label: `Approval ${i}` })
        }
        tabs.push({ key: 'approval_final', label: 'Approval Final' })
      }

      tabs.push({ key: 'approved', label: 'Disetujui' })
      tabs.push({ key: 'rejected', label: 'Ditolak' })
      return tabs
    },
  },
  async mounted() {
    await this.fetchMaxApproverCount()
    this.fetchOvertimes(true)
  },
  methods: {
    async fetchMaxApproverCount() {
      try {
        const { komtimAxiosIns } = await import('@/libs/axios')
        const response = await komtimAxiosIns.get('v1/overtime_configurations/divisions')
        const rawData = response?.data?.data
        let items = []
        if (Array.isArray(rawData)) {
          items = rawData
        } else if (Array.isArray(rawData?.items)) {
          items = rawData.items
        }
        const apiTotal = Number(
          rawData?.total_approver
          || rawData?.total_approvers
          || response?.data?.total_approver
          || 0,
        )
        let maxCount = apiTotal > 0 ? apiTotal : 2
        const map = {}
        items.forEach(div => {
          const apprList = Array.isArray(div.approvers) ? div.approvers : []
          let divCount = 0
          if (apprList.length > 0) {
            const hasFinalInList = apprList.some(a => a.is_final)
            divCount = apprList.length + (!hasFinalInList && div.approval_final ? 1 : 0)
          } else if (div.approver_1_id || div.approver_1_name || (div.approver1 && div.approver1 !== '-')) {
            divCount = 2
          } else if (div.approval_final || div.approver_final_id) {
            divCount = 1
          } else {
            divCount = Number(div.total_approver || div.total_approvers || 1)
          }

          const divId = div.division_id || div.id
          if (divId) map[divId] = divCount
          const divName = String(div.division_name || div.division || div.name || '').trim().toLowerCase()
          if (divName) map[divName] = divCount

          if (divCount > maxCount) {
            maxCount = divCount
          }
        })
        this.divisionApproverCountMap = map
        if (maxCount > this.totalApprover) {
          this.totalApprover = maxCount
        }
      } catch (e) {
        // fallback
      }
    },
    handleScroll(event) {
      const table = event?.target || document.getElementById('table')
      if (
        table
        && table.scrollTop >= table.scrollHeight - table.offsetHeight - 20
        && !this.isLoading
        && !this.isLoadingMore
        && !this.lastData
      ) {
        this.getNextData()
      }
    },
    async fetchOvertimes(isReset = true) {
      if (isReset) {
        this.isLoading = true
        this.page = 1
        this.offset = 0
        this.lastData = false
      } else {
        this.isLoadingMore = true
      }

      const params = {
        page: this.page,
        offset: this.offset,
        limit: this.limit,
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        params.search = this.searchQuery.trim()
      }

      if (this.activeTab !== 'all') {
        if (this.activeTab === 'approved') {
          params.status = 'approved'
        } else if (this.activeTab === 'rejected') {
          params.status = 'rejected'
        } else if (this.activeTab === 'approval_1') {
          params.status = 'approver_1'
          params.step = 1
        } else if (this.activeTab === 'approval_final') {
          params.status = 'approver_final'
          params.step = this.totalApprover
        } else if (this.activeTab.startsWith('approval_')) {
          const stepNum = parseInt(this.activeTab.replace('approval_', ''), 10)
          params.status = `approver_${stepNum}`
          params.step = stepNum
        } else {
          params.status = this.activeTab
        }
      }

      try {
        const result = await getAdminOvertimes(params)
        let maxApprover = Number(result.total_approver || this.totalApprover || 2)
        const rawItems = result.items || []
        rawItems.forEach(item => {
          const itemAppr = Number(
            item.total_approvers
            || item.total_approver
            || item.total_approvals
            || item.total_step
            || item.total_steps
            || (Array.isArray(item.approvers) && item.approvers.length)
            || (Array.isArray(item.approval_chain) && item.approval_chain.length)
            || (Array.isArray(item.approvalChain) && item.approvalChain.length)
            || 0,
          )
          if (itemAppr > maxApprover) {
            maxApprover = itemAppr
          }
        })
        this.totalApprover = Math.max(2, maxApprover)
        this.totalCount = typeof result.total === 'number' ? result.total : (result.items || []).length

        const items = result.items || []
        const mappedItems = items.map(item => this.mapOvertimeItem(item))

        if (isReset) {
          this.submissions = mappedItems
        } else {
          this.submissions.push(...mappedItems)
        }

        this.offset = this.submissions.length

        if (items.length < this.limit || this.submissions.length >= this.totalCount) {
          this.lastData = true
        } else {
          this.lastData = false
        }
      } catch (error) {
        showError(error, 'Gagal memuat data lembur')
        if (isReset) {
          this.submissions = []
          this.totalCount = 0
        }
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },
    getNextData() {
      if (!this.lastData && !this.isLoading && !this.isLoadingMore) {
        this.page += 1
        this.fetchOvertimes(false)
      }
    },
    onSelectTab(tabKey) {
      this.activeTab = tabKey
      this.fetchOvertimes(true)
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.fetchOvertimes(true)
      }, 400)
    },
    getDivisionApproverCount(item) {
      if (!item) return 0
      const divId = item.division_id || item.raw?.division_id
      if (divId && this.divisionApproverCountMap[divId]) {
        return this.divisionApproverCountMap[divId]
      }
      const divName = String(item.division_name || item.division || item.raw?.division_name || '').trim().toLowerCase()
      if (divName && this.divisionApproverCountMap[divName]) {
        return this.divisionApproverCountMap[divName]
      }
      return 0
    },
    getItemApprovalCount(item) {
      if (!item) return 1

      const explicit = Number(
        item.total_approvers
        || item.total_approver
        || item.total_approvals
        || item.total_step
        || item.total_steps
        || (Array.isArray(item.approval_chain) && item.approval_chain.length)
        || (Array.isArray(item.approvalChain) && item.approvalChain.length)
        || 0,
      )
      if (explicit > 0) return explicit

      if (Array.isArray(item.approvals) && item.approvals.length > 0) {
        return item.approvals.length
      }

      if (Array.isArray(item.approvers) && item.approvers.length > 0) {
        return item.approvers.length
      }

      const divCount = this.getDivisionApproverCount(item)
      if (divCount > 0) {
        return divCount
      }

      return 1
    },
    mapOvertimeItem(item) {
      const name = item.name || item.user_name || item.employee_name || '-'
      const role = item.position_name || item.role || item.user_role || 'Staff'
      const division = item.division_name || item.division || ''
      const date = this.formatDate(item.overtime_date || item.date || item.created_at)
      const duration = this.formatDuration(item.duration, item.duration_minutes || item.total_hours, item.start_time, item.end_time)
      const photoProfileUrl = item.photo_profile_url || ''

      const rawStatusStr = String(item.status ?? '').toLowerCase()
      const rawApprovalStatusStr = String(item.approval_status ?? '').toLowerCase()
      const rawStatusLabel = String(item.status_label || item.current_step_label || '').toLowerCase()
      const isApproved = (
        rawApprovalStatusStr === 'approved'
        || rawApprovalStatusStr === 'disetujui'
        || rawStatusStr === 'approved'
        || rawStatusStr === 'disetujui'
        || rawStatusStr === '1'
        || rawStatusLabel.includes('disetujui')
        || rawStatusLabel === 'approved'
        || item.is_approved === true
      )
      const isRejected = (
        rawApprovalStatusStr === 'rejected'
        || rawApprovalStatusStr === 'ditolak'
        || rawStatusStr === 'rejected'
        || rawStatusStr === 'ditolak'
        || rawStatusStr === '2'
        || rawStatusLabel.includes('ditolak')
        || rawStatusLabel === 'rejected'
        || item.is_rejected === true
      )

      let status = 'approval_1'
      if (isApproved) {
        status = 'approved'
      } else if (isRejected) {
        status = 'rejected'
      } else if (
        rawStatusLabel.includes('final')
        || rawStatusStr === 'approval_final'
        || Number(item.current_step) === 2
      ) {
        status = 'approval_final'
      } else {
        status = 'approval_1'
      }

      const itemTotalApprover = this.getItemApprovalCount(item)
      const approvalChain = this.mapApprovalChain(item, status, itemTotalApprover)

      return {
        id: item.id,
        user_id: item.user_id,
        name,
        photo_profile_url: photoProfileUrl,
        role,
        division,
        division_name: division,
        date,
        overtime_date: item.overtime_date || '',
        duration,
        duration_minutes: item.duration_minutes || 0,
        status,
        status_label: item.status_label || item.current_step_label || '',
        approval_status: item.approval_status || status,
        current_step_label: item.current_step_label || '',
        approvalChain,
        tujuan: item.reason || item.purpose || item.tujuan || '-',
        reason: item.reason || item.purpose || item.tujuan || '-',
        detail_lembur: item.detail || item.detail_lembur || item.description || item.task_description || '-',
        detail: item.detail || item.detail_lembur || item.description || item.task_description || '-',
        rejection_reason: item.rejection_reason || item.reject_note || item.notes || '',
        submitted_at: item.created_at || item.submitted_at || '',
        created_at: item.created_at || '',
        start_time: item.start_time || '',
        end_time: item.end_time || '',
        images: item.images || item.attachments || item.files || [],
        attachments: item.images || item.attachments || item.files || [],
        raw: item,
      }
    },
    mapApprovalChain(item, status, totalApprover = 1) {
      const total = Math.max(1, Number(totalApprover) || 1)

      const normalizeStatus = s => {
        if (!s && s !== 0) return 'pending'
        if (typeof s === 'number') {
          if (s === 1) return 'approved'
          if (s === 2) return 'rejected'
          return 'pending'
        }
        if (typeof s === 'string') {
          const lower = s.toLowerCase()
          if (lower === 'approved' || lower === 'disetujui' || lower === '1') return 'approved'
          if (lower === 'rejected' || lower === 'ditolak' || lower === '2') return 'rejected'
          return 'pending'
        }
        if (typeof s === 'object') {
          const sStatus = String(s.status ?? '').toLowerCase()
          const sLabel = String(s.status_label ?? '').toLowerCase()
          if (
            s.is_rejected
            || sStatus === 'rejected'
            || sStatus === 'ditolak'
            || sStatus === '2'
            || sLabel === 'rejected'
            || sLabel === 'ditolak'
          ) {
            return 'rejected'
          }
          if (
            s.is_approved
            || sStatus === 'approved'
            || sStatus === 'disetujui'
            || sStatus === '1'
            || sLabel === 'approved'
            || sLabel === 'disetujui'
          ) {
            return 'approved'
          }
          return 'pending'
        }
        return 'pending'
      }

      const trimAtRejection = chain => {
        const rejectIdx = chain.indexOf('rejected')
        if (rejectIdx !== -1) {
          return chain.slice(0, rejectIdx + 1)
        }
        return chain
      }

      // 1. If explicit approval_chain or approvalChain array is provided
      let rawChain = null
      if (Array.isArray(item.approval_chain) && item.approval_chain.length > 0) {
        rawChain = item.approval_chain
      } else if (Array.isArray(item.approvalChain) && item.approvalChain.length > 0) {
        rawChain = item.approvalChain
      }

      if (rawChain) {
        const mapped = rawChain.map(normalizeStatus)
        const rejectIdx = mapped.indexOf('rejected')
        if (rejectIdx !== -1) {
          return mapped.slice(0, rejectIdx + 1)
        }
        while (mapped.length < total) {
          mapped.push('pending')
        }
        return mapped
      }

      // 2. If approvals array with records exists
      if (Array.isArray(item.approvals) && item.approvals.length > 0) {
        const mapped = []
        for (let i = 0; i < total; i += 1) {
          const stepNum = i + 1
          const match = item.approvals.find(a => Number(a.step) === stepNum) || item.approvals[i]
          if (match) {
            const s = normalizeStatus(match)
            mapped.push(s)
            if (s === 'rejected') {
              return mapped
            }
          } else if (status === 'approved') {
            mapped.push('approved')
          } else if (status === 'rejected') {
            break
          } else {
            mapped.push('pending')
          }
        }
        return trimAtRejection(mapped)
      }

      // 3. Fallback based on status & current_step
      if (status === 'approved') {
        return Array.from({ length: total }, () => 'approved')
      }

      if (status === 'rejected') {
        const step = Number(item.current_step || item.step || 1)
        const mapped = []
        for (let i = 0; i < step - 1 && i < total; i += 1) {
          mapped.push('approved')
        }
        mapped.push('rejected')
        return mapped
      }

      let activeStep = 1
      const stepLabel = String(item.status_label || item.current_step_label || '').toLowerCase()
      if (stepLabel.includes('final') || String(status || '').includes('final')) {
        activeStep = total
      } else {
        const matchStep = stepLabel.match(/\d+/) || String(item.current_step || '').match(/\d+/) || String(status || '').match(/\d+/)
        if (matchStep) {
          activeStep = Math.min(total, Math.max(1, parseInt(matchStep[0], 10)))
        }
      }

      const chain = []
      for (let i = 1; i <= total; i += 1) {
        if (i < activeStep) {
          chain.push('approved')
        } else {
          chain.push('pending')
        }
      }
      return chain
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      try {
        const d = moment(dateStr)
        if (d.isValid()) {
          d.locale('id')
          return d.format('DD MMMM YYYY')
        }
      } catch (e) {
        // fallback
      }
      return dateStr
    },
    formatDuration(dur, minutes, startTime, endTime) {
      if (typeof minutes === 'number' && !Number.isNaN(minutes) && minutes > 0) {
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        return `${h}j ${m}m`
      }

      if (typeof dur === 'number' && !Number.isNaN(dur) && dur > 0) {
        const h = Math.floor(dur / 60)
        const m = dur % 60
        return `${h}j ${m}m`
      }

      if (typeof dur === 'string' && dur.trim()) {
        const s = dur.trim()
        if (/^\d+j\s*\d+m$/i.test(s)) {
          return s.toLowerCase()
        }
        const timeMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
        if (timeMatch) {
          const h = parseInt(timeMatch[1], 10)
          const m = parseInt(timeMatch[2], 10)
          return `${h}j ${m}m`
        }
        let hours = 0
        let mins = 0
        let matched = false

        const jamMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:jam|j\b)/i)
        if (jamMatch) {
          const val = parseFloat(jamMatch[1])
          hours = Math.floor(val)
          mins = Math.round((val - hours) * 60)
          matched = true
        }
        const menitMatch = s.match(/(\d+)\s*(?:menit|m\b)/i)
        if (menitMatch) {
          mins += parseInt(menitMatch[1], 10)
          matched = true
        }
        if (matched) {
          hours += Math.floor(mins / 60)
          mins %= 60
          return `${hours}j ${mins}m`
        }
        if (/^\d+$/.test(s)) {
          const num = parseInt(s, 10)
          if (num >= 60) {
            return `${Math.floor(num / 60)}j ${num % 60}m`
          }
          return `${num}j 0m`
        }
      }

      if (startTime && endTime) {
        const sMatch = String(startTime).match(/(\d{1,2}):(\d{2})/)
        const eMatch = String(endTime).match(/(\d{1,2}):(\d{2})/)
        if (sMatch && eMatch) {
          const startM = parseInt(sMatch[1], 10) * 60 + parseInt(sMatch[2], 10)
          let endM = parseInt(eMatch[1], 10) * 60 + parseInt(eMatch[2], 10)
          if (endM < startM) endM += 24 * 60
          const diff = endM - startM
          return `${Math.floor(diff / 60)}j ${diff % 60}m`
        }
      }

      return dur ? String(dur) : '-'
    },
    openApprovalChainModal() {
      this.$router.push({ name: 'overtime-data-setting-approval-chain' })
    },
    async handleExportSubmit(range) {
      if (this.isExporting) return
      this.isExporting = true

      try {
        const start = range?.startDate || this.exportDateRange?.startDate
        const end = range?.endDate || this.exportDateRange?.endDate

        const startDateStr = start ? moment(start).format('YYYY-MM-DD') : ''
        const endDateStr = end ? moment(end).format('YYYY-MM-DD') : ''

        const params = {
          start_date: startDateStr,
          end_date: endDateStr,
        }

        if (this.searchQuery && this.searchQuery.trim()) {
          params.search = this.searchQuery.trim()
        }

        if (this.activeTab !== 'all') {
          if (this.activeTab === 'approved') {
            params.status = 'approved'
          } else if (this.activeTab === 'rejected') {
            params.status = 'rejected'
          } else if (this.activeTab === 'approval_1') {
            params.status = 'approver_1'
          } else if (this.activeTab === 'approval_final') {
            params.status = 'approver_final'
          } else {
            params.status = this.activeTab
          }
        }

        const response = await exportAdminOvertimes(params)
        const disposition = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
        let filename = `data_lembur_${startDateStr || 'all'}_${endDateStr || 'all'}.xlsx`
        if (disposition && disposition.indexOf('filename=') !== -1) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition)
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '')
          }
        }

        downloadFileFromBlob(response.data, filename)
        showSuccess('Data Berhasil Diunduh!')
      } catch (error) {
        if (error?.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text()
            const json = JSON.parse(text)
            error.response.data = json
          } catch (e) {
            // ignore
          }
        }
        showError(error, 'Gagal mengunduh data lembur')
      } finally {
        this.isExporting = false
      }
    },
    async handleRowAction(item) {
      if (!item) return
      this.selectedDetail = { ...item }
      this.detailDrawerVisible = true
      this.isLoadingDetail = true
      try {
        const detailData = await getAdminOvertimeDetail(item.id)
        if (detailData) {
          this.selectedDetail = {
            ...item,
            ...detailData,
          }
        }
      } catch (error) {
        showError(error, 'Gagal memuat detail pengajuan lembur')
      } finally {
        this.isLoadingDetail = false
      }
    },
    handleCloseDetailDrawer() {
      this.detailDrawerVisible = false
      this.selectedDetail = {}
      this.isLoadingDetail = false
    },
  },
}
</script>
